import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import apiClient, { shouldRedirectToLogin, getErrorMessage } from '../services/api-client';
import AppConfig from '../services/app-config';
import { alpha } from '@mui/material/styles';
// Material UI imports
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import HomeIcon from '@mui/icons-material/Home';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Avatar from '@mui/material/Avatar';
import EditIcon from '@mui/icons-material/Edit';
import TextField from '@mui/material/TextField';
import ShareIcon from '@mui/icons-material/Share';
import Chip from '@mui/material/Chip';
import { QRCodeSVG } from 'qrcode.react';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import KeyIcon from '@mui/icons-material/Key';

import AuthService from '../services/auth.js';
import IconButton from '@mui/material/IconButton';
import BadgeIcon from '@mui/icons-material/Badge';

import DeleteIcon from '@mui/icons-material/Delete';
import LoginIcon from '@mui/icons-material/Login';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Zoom from '@mui/material/Zoom';
import Fab from '@mui/material/Fab';

const AirbnbReservation = () => {
  const { reservationId } = useParams();
  const [language, setLanguage] = useState('es');
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openRules, setOpenRules] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState('');
  const [dialogLoading, setDialogLoading] = useState(false);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [openQrModal, setOpenQrModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editField, setEditField] = useState('');
  const [editValue, setEditValue] = useState('');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(true);
  const buttonRef = useRef(null);

  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_MGO_API_URL;
  
  const handleCopyLink = () => {
    const link = `${window.location.origin}/guestform?id=${reservationId}`;
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  useEffect(() => {
    if (buttonRef.current && AuthService.userRoleIncludes(["admin","receptor"])) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsButtonVisible(entry.isIntersecting);
        },
        { threshold: 0.1 }
      );
      
      observer.observe(buttonRef.current);
      return () => {
        if (buttonRef.current) {
          observer.unobserve(buttonRef.current);
        }
      };
    }
  }, [reservation]);

  const fetchReservation = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`airbnb/get/${reservationId}`, {}, { action: 'fetch_airbnb_reservation' });
      console.log(response.data.data.form_data);
      response.data.data["guest_name"] = response.data.data.form_data["1_guest"]["1_name"]["value"].split(' ')[0];
      setReservation(response.data.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch reservation details');
      console.error('Error fetching reservation:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (AuthService.getCurrentUserRole() === "habitante" && AppConfig.get("unit_mode") !== "airbnb") {
      navigate("/");
    }    

    if (reservationId) {
      fetchReservation();
    }
  }, [reservationId, apiUrl]);

  const handleOpenRules = () => setOpenRules(true);
  const handleCloseRules = () => setOpenRules(false);

  const handleOpenConfirmDialog = (action) => {
    setDialogAction(action);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setDialogLoading(false);
  };

  const handleConfirmAction = () => {
    if (dialogAction === 'delete') {
      handleDeleteReservation(reservationId);
    } else if (dialogAction === 'checkin') {
      handleCheckIn(reservationId);
    }
  };

  const handleDeleteReservation = async (reservationId) => {
    setDialogLoading(true);
    try {
      try {
        await apiClient.post('visit/invalidate', { access_code: reservation.visit_code }, {}, { action: 'invalidate_visit' });
      } catch (error) {
        console.error('Error invalidating access code:', error);
      }
      await apiClient.post(`airbnb/delete/reservation/${reservationId}`, {}, {}, { action: 'delete_airbnb_reservation' });
      navigate("/airbnb");
    } catch (error) {
      console.error('Error deleting reservation:', error);
    } finally {
      setDialogLoading(false);
      setOpenConfirmDialog(false);
    }
  };

  const handleCheckIn = async (reservationId) => {
    setOpenBackdrop(true);
    try {
      await apiClient.post('visit/checkin', { access_code: reservation.visit_code }, {}, { action: 'checkin_visit' });
      await apiClient.post(`airbnb/checkin/${reservationId}`, {}, {}, { action: 'checkin_airbnb_reservation' });
      navigate("/airbnb");
    } catch (error) {
      console.error('Error checking in reservation:', error);
    } finally {
      setOpenBackdrop(false);
      setOpenConfirmDialog(false);
    }
  };

  const handleOpenEdit = (field, value) => {
    setEditField(field);
    if (field === 'guest_number') {
      setEditValue(value.toString());
    } else {
      const date = new Date(value * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      setEditValue(`${year}-${month}-${day}T${hours}:${minutes}`);
    }
    setOpenEditModal(true);
  };

  const handleCloseEdit = () => {
    setOpenEditModal(false);
    setEditField('');
    setEditValue('');
  };

  const handleSaveEdit = async () => {
    setOpenBackdrop(true);
    try {
      const payload = {
        id: reservationId,
        field: editField,
        value: editField === 'guest_number' ? parseInt(editValue) : Math.floor(new Date(editValue).getTime() / 1000)
      };
      await apiClient.post('airbnb/reservation/update', payload, {}, { action: 'update_airbnb_reservation' });
      await fetchReservation();
      handleCloseEdit();
    } catch (error) {
      console.error('Error updating reservation:', error);
    } finally {
      await fetchReservation();
      setOpenBackdrop(false);
      handleCloseEdit();
    }
  };

  const handleShareModalOpen = () => {
    setShareMessage(`Hola ${reservation.guest_name}, aquí tienes el código de acceso de la cerradura inteligente para la puerta de ${AppConfig.getVersedUnitType()}. Este código será válido durante toda tu estancia en ${AuthService.getCurrentUserOrg()}. Recuerda que para el check-in aún debes presentarte junto con los demás huéspedes en el lobby.\n\n🏠 ${AppConfig.get("unit_type")}: ${reservation.unit}\n🔑 Código: ${reservation.ttlock_code}\n\nSi el teclado está apagado, pasa suavemente la mano sobre la cerradura inteligente, ingresa el código ${reservation.ttlock_code} y, por último, presiona ☑️.`);
    setShareModalOpen(true);
  };

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(shareMessage);
    setSnackbarOpen(true);
    setShareModalOpen(false);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!reservation) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="info">No reservation found</Alert>
      </Container>
    );
  }

  const cardSx = {
    borderRadius: '10px',
    boxShadow: 0,
    border: '1px solid',
    borderColor: 'divider',
  };

  return (
    <Container maxWidth="sm" disableGutters>
      <Backdrop open={openBackdrop} style={{ zIndex: 9999 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 10 }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            px: 3,
            pt: 2,
            pb: 1.5,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <img src="/airbnb-logo.png" alt="Airbnb" style={{ width: 28, height: 28 }} />
            <Typography sx={{ fontSize: 22, fontWeight: 900, lineHeight: 1.1, letterSpacing: -0.5, color: 'text.primary' }}>
              Reservación
            </Typography>
          </Box>
        </Box>

        <Box sx={{ px: 3, mt: 1.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Card sx={cardSx}>
            <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 2 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <HomeIcon sx={{ color: '#238048' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {AppConfig.get('unit_type')}
                  </Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 800, color: 'text.primary' }}>
                    {reservation.unit}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <AccessTimeFilledIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Check-in</Typography>
                    {AuthService.userRoleIncludes(["habitante"]) && (
                      <IconButton size="small" sx={{ p: 0, ml: 0.5 }} onClick={() => handleOpenEdit('checkin', reservation.checkin)}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                    {AppConfig.formatFullDate(reservation.checkin)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <AccessTimeFilledIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Check-out</Typography>
                    {AuthService.userRoleIncludes(["habitante"]) && (
                      <IconButton size="small" sx={{ p: 0, ml: 0.5 }} onClick={() => handleOpenEdit('expires', reservation.expires)}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                    {AppConfig.formatFullDate(reservation.expires)}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                    {reservation.info_submitted ? <CheckCircleIcon color="secondary" sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }}/> : <PendingIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }}/>}
                    {reservation.info_submitted ? "Formulario Completado" : "Formulario Pendiente"}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setOpenQrModal(true)}
                  sx={{ width: 34, height: 34, borderRadius: '10px', border: '1px solid', borderColor: 'divider', color: 'text.primary' }}
                >
                  {AuthService.getCurrentUserRole() === "habitante" ? <ShareIcon fontSize="small" /> : <QrCodeIcon fontSize="small" />}
                </IconButton>
              </Box>
              <Button 
                variant="outlined"
                color="secondary" 
                size="small" 
                onClick={handleOpenRules}
                sx={{ mt: 1.5, borderRadius: '10px', fontWeight: 700, fontSize: 12 }}
              >
                {reservation.info_submitted ? "Ver Reglamento Aceptado" : "Ver Reglamento"}
              </Button>
            </CardContent>
          </Card>

          {reservation.ttlock_code && (
            <Card sx={cardSx}>
              <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <KeyIcon sx={{ fontSize: 18, color: '#238048' }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Código de Acceso</Typography>
                </Box>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '10px', mb: 1.5, backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.06) }}>
                  <Typography sx={{ fontSize: 24, fontWeight: 900, textAlign: 'center', color: 'text.primary', letterSpacing: 4 }}>
                    {reservation.ttlock_code}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
                  <Button variant="outlined" color="secondary" onClick={() => navigator.clipboard.writeText(reservation.ttlock_code)} size="small" sx={{ borderRadius: '10px', fontWeight: 700, fontSize: 12 }}>
                    Copiar Código
                  </Button>
                  <Button variant="outlined" color="secondary" onClick={handleShareModalOpen} size="small" sx={{ borderRadius: '10px', fontWeight: 700, fontSize: 12 }}>
                    Compartir Mensaje
                  </Button>
                </Stack>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 1.5, display: 'flex', alignItems: 'center' }}>
                  Integración proveída por TTLock
                  <img src="/ttlock-logo.png" alt="TTLock" style={{ width: 14, height: 14, marginLeft: 4 }} />
                </Typography>
              </CardContent>
            </Card>
          )}

          <Card sx={cardSx}>
            <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'text.primary' }}>
                  {reservation.guest_number} {reservation.guest_number > 1 ? "Huéspedes" : "Huésped"}
                </Typography>
                {AuthService.userRoleIncludes(["habitante"]) && (
                  <IconButton size="small" onClick={() => handleOpenEdit('guest_number', reservation.guest_number)} sx={{ width: 34, height: 34, borderRadius: '10px', border: '1px solid', borderColor: 'divider' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {reservation.form_data && (() => {
                const additionalGuests = [];
                for (let i = 1; i <= 20; i++) {
                  const guestKey = `${i}_guest`;
                  if (reservation.form_data[guestKey]) {
                    additionalGuests.push(
                      <Box key={`guest-container-${i}`}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 14, fontWeight: 800 }}>
                              {reservation.form_data[guestKey]["1_name"].value ? reservation.form_data[guestKey]["1_name"].value.charAt(0).toUpperCase() : 'H'}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                                {guestKey === "1_guest" ? "Huésped Principal" : `Huésped ${i}`}
                              </Typography>
                              <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>
                                {reservation.form_data[guestKey]["1_name"].value}
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton
                            sx={{ width: 34, height: 34, borderRadius: '10px', border: '1px solid', borderColor: 'divider' }}
                            onClick={() => {
                              if (reservation.form_data[guestKey]["3_id_picture"].value) {
                                setCurrentImage(`${apiUrl}/img/airbnb_${reservation.form_data[guestKey]["3_id_picture"].value}`);
                                setOpenImageModal(true);
                              }
                            }}
                          >
                            <BadgeIcon fontSize="small" color={reservation.form_data[guestKey]["3_id_picture"].submitted ? "secondary" : "default"} />
                          </IconButton>
                        </Box>
                        {i < 20 && reservation.form_data[`${i + 1}_guest`] && <Divider />}
                      </Box>
                    );
                  } else {
                    break;
                  }
                }
                return additionalGuests;
              })()}

              {reservation.guest_checked_in && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Chip 
                    icon={<CheckCircleIcon style={{ color: '#238048' }} />}
                    label={reservation.guest_number > 1 ? "Huéspedes Ingresados" : "Huésped Ingresado"}
                    sx={{ fontWeight: 700, fontSize: 12, py: 2.5 }} 
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {reservation.ttlock_code && (
            <Dialog open={shareModalOpen} onClose={() => setShareModalOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Compartir Código de Acceso</DialogTitle>
              <DialogContent>
                <TextField fullWidth multiline rows={12} value={shareMessage} onChange={(e) => setShareMessage(e.target.value)} variant="outlined" sx={{ mt: 2 }} />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShareModalOpen(false)}>Cerrar</Button>
                <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={handleCopyMessage}>Copiar Mensaje</Button>
              </DialogActions>
            </Dialog>
          )}

          {AuthService.userRoleIncludes(["habitante"]) && reservation.expires > Date.now() / 1000 && (
            <Button
              variant="contained"
              color="error"
              fullWidth
              startIcon={<DeleteIcon />}
              onClick={() => handleOpenConfirmDialog('delete')}
              sx={{ borderRadius: '10px', fontWeight: 800, py: 1.5 }}
            >
              Eliminar Reservación
            </Button>
          )}
          {AuthService.userRoleIncludes(["admin","receptor"]) && (
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', textAlign: 'center', mb: 1 }}>
                {reservation.info_submitted 
                  ? reservation.guest_checked_in ? "Los huéspedes ya han completado su ingreso" : "Haz click para registrar el ingreso de los huéspedes"
                  : "El formulario no ha sido completado, solicita a los huéspedes llenarlo."}
              </Typography>
              <Box ref={buttonRef}>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  startIcon={<LoginIcon />}
                  onClick={() => handleOpenConfirmDialog('checkin')}
                  disabled={!reservation.info_submitted || reservation.guest_checked_in}
                  sx={{ borderRadius: '10px', fontWeight: 800, py: 1.5 }}
                >
                  Dar Ingreso a Huéspedes
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {AuthService.userRoleIncludes(["admin","receptor"]) && !isButtonVisible && reservation && (
        <Zoom in={!isButtonVisible}>
          <Fab
            color="secondary"
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(156, 39, 176, 0.4)'
                },
                '70%': {
                  boxShadow: '0 0 0 10px rgba(156, 39, 176, 0)'
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(156, 39, 176, 0)'
                }
              }
            }}
            onClick={() => handleOpenConfirmDialog('checkin')}
            disabled={!reservation.info_submitted || reservation.guest_checked_in}
          >
            <LoginIcon /> 
          </Fab>
        </Zoom>
      )}

      <Dialog
        open={openRules}
        onClose={handleCloseRules}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reglamento</DialogTitle>
        <DialogContent>
          <List>
            {reservation.form_data.guest_rules.rules.es.rules.map((rule, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemIcon>
                    {reservation.form_data.guest_rules.accepted[index] ? (
                      <CheckIcon color="primary" />
                    ) : (
                      <PendingIcon color="default" />
                    )}
                  </ListItemIcon>
                  <ListItemText primary={rule} />
                </ListItem>
                {index < reservation.form_data.guest_rules.rules[language].rules.length - 1 && (
                  <Divider component="li" />
                )}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRules}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {dialogAction === 'delete' ? '¿Eliminar reservación?' : '¿Dar ingreso a huespedes?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {dialogAction === 'delete' 
              ? '¿Estás seguro que deseas eliminar esta reservación? Esta acción no se puede deshacer.'
              : '¿Estás seguro que deseas dar ingreso a los huespedes? Esta acción no se puede deshacer.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={dialogLoading}>Cancelar</Button>
          <Button 
            onClick={handleConfirmAction} 
            color={dialogAction === 'delete' ? 'error' : 'primary'}
            disabled={dialogLoading}
            startIcon={dialogLoading ? <CircularProgress size={20} /> : null}
          >
            {dialogLoading ? 'Procesando...' : (dialogAction === 'delete' ? 'Eliminar' : 'Confirmar')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {currentImage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <img 
                src={currentImage} 
                alt="ID Document" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '80vh', 
                  objectFit: 'contain' 
                }} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImageModal(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openQrModal}
        onClose={() => setOpenQrModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
            {AuthService.userRoleIncludes(["habitante"]) ? (
              <>
                <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
                  Comparte este enlace con tus huéspedes para que completen el formulario:
                </Typography>
                <Box sx={{ 
                  bgcolor: 'grey.100', 
                  p: 2, 
                  borderRadius: 1, 
                  width: '100%',
                  mb: 2,
                  wordBreak: 'break-all'
                }}>
                  <Typography variant="body2">
                    {`${window.location.origin}/guestform?id=${reservationId}`}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyLink}
                >
                  {linkCopied ? "¡Copiado!" : "Copiar Enlace"}
                </Button>
              </>
            ) : (
              <>
                <QRCodeSVG
                  value={`${window.location.origin}/guestform?id=${reservationId}`}
                  size={256}
                  level="L"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Solicita al huesped que escanea el QR para completar el formulario
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQrModal(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openEditModal}
        onClose={handleCloseEdit}
        aria-labelledby="edit-dialog-title"
      >
        <DialogTitle id="edit-dialog-title">
          {editField === 'checkin' ? 'Editar Check-in' : 
           editField === 'expires' ? 'Editar Check-out' : 
           'Editar Número de Huespedes'}
        </DialogTitle>
        <DialogContent>
          {editField === 'guest_number' ? (
            <TextField
              autoFocus
              margin="dense"
              label="Número de huespedes"
              type="number"
              fullWidth
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              inputProps={{ min: 1 }}
            />
          ) : (
            <TextField
              autoFocus
              margin="dense"
              label={editField === 'checkin' ? 'Fecha de Check-in' : 'Fecha de Check-out'}
              type="datetime-local"
              fullWidth
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancelar</Button>
          <Button onClick={handleSaveEdit} color="primary">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Mensaje copiado al portapapeles
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AirbnbReservation;
